import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/providers/auth-provider";
import { getPlayerQueueStatus } from "@/services/duel/get-player-queue-status";

export type DuelQueueStatus = "IDLE" | "WAITING" | "MATCHED" | "IN_DUEL";

/**
 * Hook to manage duel queue status polling.
 * Polls every `intervalMs` (default 8s) when not idle and enabled.
 */
export function useDuelQueue(
  enabled: boolean = true,
  initialData?: { status: DuelQueueStatus; duelId: string | null },
  intervalMs: number = 8000,
) {
  const { getAccessToken } = useAuth();
  const [queueStatus, setQueueStatus] = useState<DuelQueueStatus>(
    initialData?.status ?? "IDLE",
  );
  const [currentDuelId, setCurrentDuelId] = useState<string | null>(
    initialData?.duelId ?? null,
  );
  const [joinedAt, setJoinedAt] = useState<number | null>(null);
  const [deckId, setDeckId] = useState<number | null>(null);
  const queueStatusRef = useRef(queueStatus);

  useEffect(() => {
    queueStatusRef.current = queueStatus;
  }, [queueStatus]);

  const fetchQueueStatus = useCallback(async () => {
    if (!getAccessToken()) {
      setQueueStatus("IDLE");
      return;
    }

    try {
      const response = await getPlayerQueueStatus();
      if (response.ok && response.data) {
        setQueueStatus(response.data.status);
        setCurrentDuelId(response.data.queue_entry?.duel_id ?? null);
        setJoinedAt(response.data.queue_entry?.joined_at ?? null);
        setDeckId(response.data.queue_entry?.deck_id ?? null);
      } else {
        // Keep the last known status on failure — a transient error shouldn't
        // visually eject the player from the queue while they're still in it.
        console.error("Failed to fetch queue status:", response.message);
      }
    } catch (error) {
      console.error("Failed to fetch queue status:", error);
    }
  }, [getAccessToken]);

  useEffect(() => {
    if (!enabled) return;

    fetchQueueStatus();

    const interval = setInterval(() => {
      if (queueStatusRef.current === "IDLE") return;
      fetchQueueStatus();
    }, intervalMs);

    return () => clearInterval(interval);
  }, [enabled, fetchQueueStatus, intervalMs]);

  return {
    queueStatus,
    currentDuelId,
    joinedAt,
    deckId,
    refetch: fetchQueueStatus,
  };
}

